import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { getOutcomeColor } from '@/lib/analytics/outcomeColors';
import type { BreakdownResult, BreakdownRow } from '@/hooks/useCallOutcomesBreakdown';
import { useMemo } from 'react';

interface Props {
  data: BreakdownResult;
  canonicalOrder: string[];
}

export function CallOutcomesRibbon({ data, canonicalOrder }: Props) {
  const segments = useMemo(() => {
    const byOutcome = new Map(data.rows.map((r) => [r.segment, r]));
    const ordered = canonicalOrder
      .map((name) => byOutcome.get(name))
      .filter((r): r is BreakdownRow => !!r && r.current_count > 0);
    const unrecorded = byOutcome.get('no_outcome');
    if (unrecorded && unrecorded.current_count > 0) {
      ordered.push({ ...unrecorded, segment: 'Unrecorded' });
    }
    return ordered;
  }, [data.rows, canonicalOrder]);

  if (data.total_current === 0) {
    return (
      <div className="h-12 flex items-center justify-center text-sm text-muted-foreground border rounded-md">
        No calls in this range
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div role="presentation" className="h-12 w-full flex rounded-md overflow-hidden border">
        {segments.map((s) => (
          <Tooltip key={s.segment}>
            <TooltipTrigger asChild>
              <div
                tabIndex={0}
                aria-label={`${s.segment}: ${s.current_count} (${s.current_percent}%)`}
                className="min-w-[2px] outline-none hover:brightness-110 focus-visible:ring-2 focus-visible:ring-ring transition-[filter]"
                style={{
                  flex: s.current_count,
                  backgroundColor: getOutcomeColor(s.segment, canonicalOrder),
                }}
              />
            </TooltipTrigger>
            <TooltipContent
              side="top"
              className="bg-popover text-popover-foreground border border-border"
            >
              <div className="text-xs">
                <div className="font-medium">{s.segment}</div>
                <div className="text-muted-foreground">
                  {s.current_count} ({s.current_percent}%)
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
}
